import { Link } from 'wouter';
import { Brain, ArrowLeft } from 'lucide-react';

const LAST_UPDATED = 'August 13, 2026';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
              <Brain className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg">MarketMind</span>
          </Link>
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 prose prose-neutral dark:prose-invert prose-headings:font-bold prose-a:text-primary">
        <h1 className="text-3xl font-bold mb-1">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {LAST_UPDATED}</p>

        <p>
          These Terms of Service ("Terms") govern your access to and use of MarketMind (the
          "Service"). By creating an account or using the Service, you agree to these Terms. If
          you do not agree, do not use the Service.
        </p>

        <h2>1. Not Financial Advice</h2>
        <p>
          MarketMind provides AI-generated analysis, summaries, and information about prediction
          markets for educational and informational purposes only. Nothing in the Service is, or
          should be construed as, financial, investment, legal, or trading advice. Prediction
          markets involve real financial risk, including the risk of losing your entire stake. You
          are solely responsible for any decisions you make based on information from the Service.
        </p>

        <h2>2. Your Account</h2>
        <ul>
          <li>You must provide accurate information when creating an account.</li>
          <li>You are responsible for maintaining the security of your account credentials.</li>
          <li>You must be at least 18 years old to use MarketMind.</li>
          <li>You are responsible for all activity that occurs under your account.</li>
        </ul>

        <h2>3. Subscriptions and Billing</h2>
        <ul>
          <li>
            MarketMind offers a free tier and a paid "Pro" subscription tier. Paid subscriptions
            are billed in advance on a recurring basis through Stripe.
          </li>
          <li>
            Subscriptions renew automatically until cancelled. You can cancel at any time from
            Settings; your Pro access continues until the end of the current billing period.
          </li>
          <li>
            Fees are non-refundable except where required by law or explicitly stated otherwise.
          </li>
          <li>
            We may change subscription pricing or features with reasonable advance notice.
          </li>
        </ul>

        <h2>4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose or in violation of these Terms.</li>
          <li>Attempt to reverse-engineer, scrape, or overload the Service's infrastructure.</li>
          <li>Misrepresent AI-generated analysis as professional financial advice to others.</li>
          <li>Interfere with other users' use and enjoyment of the Service.</li>
        </ul>

        <h2>5. Third-Party Data and Services</h2>
        <p>
          Market data displayed in MarketMind is sourced from third parties, including Kalshi. We
          do not control and are not responsible for the accuracy, completeness, or timeliness of
          third-party data. Trading on any prediction market platform is subject to that platform's
          own terms and is outside our control.
        </p>

        <h2>6. Intellectual Property</h2>
        <p>
          The Service, including its design, code, and AI-generated content templates, is owned by
          MarketMind and protected by applicable intellectual property laws. You retain ownership
          of content you submit (such as watchlist notes), and grant us a license to use it to
          operate and improve the Service.
        </p>

        <h2>7. Disclaimers</h2>
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind,
          whether express or implied, including warranties of merchantability, fitness for a
          particular purpose, or non-infringement. We do not guarantee that analyses are accurate,
          complete, or profitable.
        </p>

        <h2>8. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, MarketMind and its affiliates will not be liable
          for any indirect, incidental, special, consequential, or punitive damages, or any loss of
          profits or trading losses, arising from your use of the Service.
        </p>

        <h2>9. Termination</h2>
        <p>
          We may suspend or terminate your access to the Service at any time if you violate these
          Terms. You may stop using the Service and delete your account at any time.
        </p>

        <h2>10. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. Continued use of the Service after changes
          take effect constitutes acceptance of the revised Terms.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          If you have questions about these Terms, please contact us through the support options
          available in the app.
        </p>
      </main>
    </div>
  );
}
